import React from 'react';
import {
  LinkedinSquareFilled,
  FacebookSquareFilled,
  XTwitterSquareFilled,
  GithubSquareColor,
  WorldColor,
  RedditSquareColor,
  DiscordSquareColor,
  YoutubeSquareColor,
  InstagramSquareColor,
  PinterestSquareColor,
  Crunchbase,
} from '@saleshandy/icons';
import { V2Lead } from '../../../types';

type SocialPlatform =
  | 'linkedin'
  | 'facebook'
  | 'twitter'
  | 'github'
  | 'reddit'
  | 'discord'
  | 'youtube'
  | 'instagram'
  | 'pinterest'
  | 'crunchbase'
  | 'generic';

interface SocialLinkEntry {
  type: SocialPlatform;
  url: string;
}

const withNormalisedSize = (
  Icon: React.ComponentType<any>,
): React.FC<{ width?: number; height?: number }> => ({
  width = 20,
  height = 20,
}) => (
  <span
    style={{
      display: 'inline-flex',
      width,
      height,
      minWidth: width,
      minHeight: height,
      overflow: 'hidden',
    }}
  >
    <Icon style={{ width: '100%', height: '100%' }} />
  </span>
);

export const ICON_MAP: Record<SocialPlatform, React.ComponentType<any>> = {
  linkedin: LinkedinSquareFilled,
  facebook: withNormalisedSize(FacebookSquareFilled),
  twitter: XTwitterSquareFilled,
  github: GithubSquareColor,
  reddit: RedditSquareColor,
  discord: DiscordSquareColor,
  youtube: YoutubeSquareColor,
  instagram: withNormalisedSize(InstagramSquareColor),
  pinterest: PinterestSquareColor,
  crunchbase: withNormalisedSize(Crunchbase),
  generic: WorldColor,
};

export const getPlatformType = (url?: string): SocialPlatform => {
  if (!url) {
    return 'generic';
  }
  const normalized = url.toLowerCase();
  if (normalized.includes('linkedin')) return 'linkedin';
  if (normalized.includes('facebook')) return 'facebook';
  if (normalized.includes('twitter') || normalized.includes('x.com')) {
    return normalized.includes('x.com') ? 'twitter' : 'twitter';
  }
  if (normalized.includes('github')) return 'github';
  if (normalized.includes('reddit')) return 'reddit';
  if (normalized.includes('discord')) return 'discord';
  if (normalized.includes('youtube')) return 'youtube';
  if (normalized.includes('instagram')) return 'instagram';
  if (normalized.includes('pinterest')) return 'pinterest';
  if (normalized.includes('crunchbase')) return 'crunchbase';
  return 'generic';
};

const normalizeLink = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    if ('url' in (value as Record<string, unknown>)) {
      return String((value as Record<string, unknown>).url || '');
    }
  }
  return null;
};

interface SocialsCellProps {
  lead: V2Lead;
}

const SocialsCell: React.FC<SocialsCellProps> = ({ lead }) => {
  // Memoize link processing
  const uniqueLinks = React.useMemo(() => {
    const collectedLinks: SocialLinkEntry[] = [];

    const appendLink = (url?: string | null) => {
      if (!url) return;
      collectedLinks.push({
        type: getPlatformType(url),
        url,
      });
    };

    const socialLinks = Array.isArray(lead.social_link) ? lead.social_link : [];
    socialLinks.forEach((entry) => appendLink(normalizeLink(entry)));

    appendLink(lead.linkedin_url);
    appendLink(lead.github_url);

    return collectedLinks.filter(
      (entry, index, array) =>
        entry.url &&
        array.findIndex(
          (item) => item.url.toLowerCase() === entry.url.toLowerCase(),
        ) === index,
    );
  }, [lead.social_link, lead.linkedin_url, lead.github_url]);

  if (uniqueLinks.length === 0) {
    return <div className="v2-lead-socials-cell">-</div>;
  }

  return (
    <div className="v2-lead-socials-cell">
      {uniqueLinks.map((entry) => {
        const IconComponent = ICON_MAP[entry.type] || ICON_MAP.generic;
        return (
          <a
            key={entry.url}
            href={entry.url}
            target="_blank"
            rel="noreferrer"
            className="v2-lead-social-link"
            onClick={(e) => e.stopPropagation()}
          >
            <IconComponent width={20} height={20} loading="lazy" />
          </a>
        );
      })}
    </div>
  );
};

// Memoize SocialsCell to prevent unnecessary re-renders
export default React.memo(
  SocialsCell,
  (prevProps, nextProps) =>
    prevProps.lead.id === nextProps.lead.id &&
    JSON.stringify(prevProps.lead.social_link) ===
      JSON.stringify(nextProps.lead.social_link) &&
    prevProps.lead.linkedin_url === nextProps.lead.linkedin_url &&
    prevProps.lead.github_url === nextProps.lead.github_url,
);
